import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router';

import { ArrowLeft, CheckCircle, Info, XCircle } from 'lucide-react';

import ReleaseConfirmDialog from '@/components/release/ReleaseConfirmDialog';
import ReleaseItemList from '@/components/release/ReleaseItemList';
import ReleasePanelLayout from '@/components/release/ReleasePanelLayout';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ControlContext } from '@/contexts/ControlContext';
import { normalizeControl } from '@/hooks/useControlsAPI';
import { useDocumentById } from '@/hooks/useDocuments';
import { useNotificationModal } from '@/hooks/useNotificationModal';
import PageLayout from '@/layouts/PageLayout';
import { apiService } from '@/lib/axios';
import ControlApprovalsView from '@/views/controls/ControlApprovalsView';
import ControlDetailsView from '@/views/controls/ControlDetailsView';
import ControlInformationView from '@/views/controls/ControlInformationView';
import ControlRiskView from '@/views/controls/ControlRiskView';

const controlTypeMap = {
  INITIAL_RELEASE: 'Initial Release',
  NEW_DOCUMENT: 'New Document',
  PERIODIC: 'Periodic Review',
  REVISION: 'Revision',
};

const DocumentReleasePage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { showNotificationModal } = useNotificationModal();

  const { document: docData, load } = useDocumentById();
  const [controls, setControls] = useState([]);
  const [controlsLoading, setControlsLoading] = useState(true);

  const [selectedControlId, setSelectedControlId] = useState(null);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [previewTab, setPreviewTab] = useState('information');
  const [releasing, setReleasing] = useState(false);

  // Load document details
  useEffect(() => {
    load(id);
  }, [id, load]);

  // Load CCs for this document
  useEffect(() => {
    if (!id) return;
    setControlsLoading(true);
    apiService.controls.getAll({ documentId: id })
      .then((res) => {
        if (res.data.success) {
          setControls(res.data.data);
        }
      })
      .catch((err) => console.error('Failed to load controls:', err))
      .finally(() => setControlsLoading(false));
  }, [id]);

  // Keep the raw control for list/badge/release logic (isLinked, etc.) —
  // only the preview form needs the normalized shape (riskScore, etc.).
  const selectedControl = controls.find((c) => c.id === selectedControlId) || null;
  const previewControl = useMemo(
    () => (selectedControl ? normalizeControl(selectedControl) : null),
    [selectedControl]
  );

  const availableControls = controls.filter(
    (control) =>
      control.status === 'APPROVED' &&
      !control.isLinked &&
      control.documentRevision === docData?.revision
  );

  const handleRelease = async () => {
    if (!selectedControlId || !docData) return;
    setReleasing(true);
    try {
      const res = await apiService.controls.release(selectedControlId, { linkedDocumentId: docData.id });
      if (res.data.success) {
        showNotificationModal(
          'Release Successful',
          'The document has been released and moved to the Master List.',
          'success'
        );
        navigate('/documents', { state: 'master' });
      }
    } catch (err) {
      const message = err?.response?.data?.error || 'Failed to release document';
      showNotificationModal('Release Failed', message, 'error');
    } finally {
      setReleasing(false);
      setConfirmDialogOpen(false);
    }
  };

  const getCCStatusBadge = (control) => {
    if (control.isLinked) {
      return <Badge variant="secondary">Linked</Badge>;
    }
    if (control.status === 'APPROVED') {
      return <Badge variant="success"><CheckCircle className="h-3 w-3 mr-1" />Approved</Badge>;
    }
    return <Badge variant="outline"><XCircle className="h-3 w-3 mr-1" />{control.status}</Badge>;
  };

  const renderControlItem = (item) => (
    <div>
      <div className="flex items-center justify-between">
        <div className="font-medium text-sm">{item.serial}</div>
        {getCCStatusBadge(item)}
      </div>
      <div className="text-xs text-muted-foreground mt-1">
        {controlTypeMap[item.typeChange] || item.typeChange}
      </div>
      <div className="text-xs text-muted-foreground">
        {item.requestorName} &middot; {item.reason?.slice(0, 60)}{item.reason?.length > 60 ? '...' : ''}
      </div>
    </div>
  );

  return (
    <PageLayout
      title={`Release Document: ${docData?.templateId || id}`}
      toolbarHead={
        <Button onClick={() => navigate(-1)} variant="outline">
          <ArrowLeft className="h-4 w-4 mr-1" />
          Back
        </Button>
      }
      toolbarTail={
        <Button
          disabled={!selectedControlId}
          onClick={() => setConfirmDialogOpen(true)}
        >
          Release
        </Button>
      }
    >
      {docData && (
        <div className="flex items-center gap-2 mb-4 rounded-md border border-blue-200 bg-blue-50 px-4 py-3 text-sm">
          <Info className="h-4 w-4 text-blue-500 shrink-0" />
          <span>
            <span className="font-medium">Releasing:</span>{' '}
            {docData.templateId} — {docData.documentTitle} (Rev {docData.revision})
          </span>
        </div>
      )}

      {availableControls.length === 0 && controls.length > 0 && (
        <div className="flex items-center gap-2 mb-4 rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm">
          <XCircle className="h-4 w-4 text-amber-500 shrink-0" />
          <span>
            No approved, unlinked change controls are available for this document.
          </span>
        </div>
      )}

      {controlsLoading ? (
        <div className="flex items-center justify-center h-64 text-muted-foreground">
          Loading change controls...
        </div>
      ) : availableControls.length === 0 ? (
        <div className="flex items-center justify-center h-64 text-muted-foreground">
          No approved change controls are available for this document revision.
        </div>
      ) : (
        <ReleasePanelLayout
          leftPanel={
            <>
              <h3 className="font-semibold text-sm mb-3">
                Change Controls ({availableControls.length})
              </h3>
              <ReleaseItemList
                items={availableControls}
                selectedId={selectedControlId}
                onSelect={setSelectedControlId}
                renderItem={renderControlItem}
                emptyMessage="No change controls available for linking"
              />
            </>
          }
          rightPanel={
            selectedControl ? (
              <ControlContext.Provider
                value={{
                  form: previewControl,
                  id: selectedControlId,
                  isEdit: false,
                  isPreview: true,
                  readOnly: true,
                  setFormByKey: () => {},
                }}
              >
                <Tabs value={previewTab} onValueChange={setPreviewTab}>
                  <TabsList className="w-full flex mb-6 gap-x-2 gap-y-1 flex-wrap">
                    <TabsTrigger value="information">
                      Document Change Information
                    </TabsTrigger>
                    <TabsTrigger value="details">Details of Change</TabsTrigger>
                    <TabsTrigger value="risk">Risk Impact Assessment</TabsTrigger>
                    <TabsTrigger value="approvals">Approvals</TabsTrigger>
                  </TabsList>
                  <TabsContent value="information" className="mt-0">
                    <ControlInformationView />
                  </TabsContent>
                  <TabsContent value="details" className="mt-0">
                    <ControlDetailsView />
                  </TabsContent>
                  <TabsContent value="risk" className="mt-0">
                    <ControlRiskView />
                  </TabsContent>
                  <TabsContent value="approvals" className="mt-0 space-y-8">
                    <ControlApprovalsView />
                  </TabsContent>
                </Tabs>
              </ControlContext.Provider>
            ) : (
              <div className="flex items-center justify-center h-full text-gray-400">
                Select a change control to preview
              </div>
            )
          }
        />
      )}

      <ReleaseConfirmDialog
        open={confirmDialogOpen}
        onClose={() => setConfirmDialogOpen(false)}
        onConfirm={handleRelease}
        title="Confirm Document Release"
        description="You are about to release this document and link it to a change control."
        details={[
          {
            label: 'Document',
            value: docData?.documentTitle || `Document ${id}`,
          },
          {
            label: 'Document Type',
            value: docData?.templateId || '',
          },
          {
            label: 'Change Control',
            value: selectedControl?.serial || '',
          },
          {
            label: 'Type of Change',
            value: controlTypeMap[selectedControl?.typeChange] || '',
          },
        ]}
        loading={releasing}
      />
    </PageLayout>
  );
};

export default DocumentReleasePage;
